import { useEffect, useRef, useState, useMemo } from 'react';
import QRCodeStyling, { DotType, CornerSquareType, CornerDotType } from 'qr-code-styling';
import { motion } from 'framer-motion';
import { Download, Save, Loader2, Zap, Lock, Info, LogIn } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useQRStore } from '@/stores/qrStore';
import { useQRCodes } from '@/hooks/useQRCodes';
import { useAuthStore } from '@/stores/authStore';
import { useSubscription } from '@/hooks/useSubscription';
import { generateQRContent } from '@/lib/qr-utils';
import { getRedirectUrl } from '@/lib/dynamic-qr-utils';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SaveTemplateModal } from '@/components/templates/SaveTemplateModal';
import { toast } from 'sonner';
import type { FrameSettings } from '@/stores/qrStore';

/**
 * Draw a frame around the QR code on a canvas for download export.
 * Replicates the CSS frame styles from FrameOverlay as canvas drawing operations.
 */
function drawFrameOnCanvas(
  qrImg: ImageBitmap,
  qrSize: number,
  frame: FrameSettings
): HTMLCanvasElement {
  // The CSS preview uses ~4px border at 300px QR. Scale proportionally to download res.
  // Use a thin border that matches the preview look, not the raw thickness slider value.
  const scale = qrSize / 300;
  const borderW = Math.round(4 * scale);
  const textH = Math.round(28 * scale);
  const hasText = ['scan-me-top', 'scan-me-bottom', 'tag', 'badge', 'clipboard',
    'laptop', 'watch', 'camera', 'bag', 'speech-bubble', 'banner'].includes(frame.style);
  const isTopText = ['scan-me-top', 'laptop', 'watch', 'camera'].includes(frame.style);

  // Calculate canvas dimensions — pad = inner spacing between QR and frame border
  const pad = Math.round(20 * scale);
  const textPad = hasText ? textH + Math.round(10 * (qrSize / 300)) : 0;
  const topExtra = pad + (hasText && isTopText ? textPad : 0);
  const bottomExtra = pad + (hasText && !isTopText ? textPad : 0);
  const canvasW = qrSize + pad * 2;
  const canvasH = qrSize + topExtra + bottomExtra;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d')!;

  // Fill white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasW, canvasH);

  // Draw frame border
  ctx.strokeStyle = frame.color;
  ctx.lineWidth = borderW;
  const radius = Math.round(12 * (qrSize / 300));

  const fx = borderW / 2;
  const fy = topExtra - pad + borderW / 2;
  const fw = canvasW - borderW;
  const fh = qrSize + pad * 2 - borderW;

  switch (frame.style) {
    case 'simple':
      ctx.strokeRect(fx, fy, fw, fh);
      break;

    case 'rounded':
    case 'scan-me-top':
    case 'scan-me-bottom':
    case 'tag':
    case 'gradient':
      ctx.beginPath();
      ctx.roundRect(fx, fy, fw, fh, radius);
      ctx.stroke();
      break;

    case 'circle':
    case 'badge': {
      const cx = canvasW / 2;
      const cy = topExtra + qrSize / 2;
      const r = (qrSize + pad * 2) / 2 - borderW / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }

    case 'double': {
      const gap = Math.round(6 * (qrSize / 300));
      ctx.strokeRect(fx, fy, fw, fh);
      ctx.lineWidth = borderW * 0.6;
      ctx.strokeRect(fx - gap - borderW * 0.3, fy - gap - borderW * 0.3,
        fw + (gap + borderW * 0.3) * 2, fh + (gap + borderW * 0.3) * 2);
      break;
    }

    case 'ticket':
      ctx.setLineDash([borderW * 1.5, borderW]);
      ctx.beginPath();
      ctx.roundRect(fx, fy, fw, fh, radius);
      ctx.stroke();
      ctx.setLineDash([]);
      break;

    case 'neon':
      ctx.shadowColor = frame.color;
      ctx.shadowBlur = Math.round(20 * (qrSize / 300));
      ctx.lineWidth = borderW * 0.5;
      ctx.beginPath();
      ctx.roundRect(fx, fy, fw, fh, radius);
      ctx.stroke();
      ctx.shadowBlur = 0;
      break;

    case 'decorative': {
      const cornerLen = Math.round(40 * (qrSize / 300));
      ctx.lineWidth = borderW;
      // Top-left
      ctx.beginPath();
      ctx.moveTo(fx, fy + cornerLen);
      ctx.lineTo(fx, fy);
      ctx.lineTo(fx + cornerLen, fy);
      ctx.stroke();
      // Top-right
      ctx.beginPath();
      ctx.moveTo(fx + fw - cornerLen, fy);
      ctx.lineTo(fx + fw, fy);
      ctx.lineTo(fx + fw, fy + cornerLen);
      ctx.stroke();
      // Bottom-left
      ctx.beginPath();
      ctx.moveTo(fx, fy + fh - cornerLen);
      ctx.lineTo(fx, fy + fh);
      ctx.lineTo(fx + cornerLen, fy + fh);
      ctx.stroke();
      // Bottom-right
      ctx.beginPath();
      ctx.moveTo(fx + fw - cornerLen, fy + fh);
      ctx.lineTo(fx + fw, fy + fh);
      ctx.lineTo(fx + fw, fy + fh - cornerLen);
      ctx.stroke();
      break;
    }

    default:
      // Fallback: simple rounded border
      ctx.beginPath();
      ctx.roundRect(fx, fy, fw, fh, radius);
      ctx.stroke();
      break;
  }

  // Draw QR image centered in the frame
  ctx.drawImage(qrImg, pad, topExtra, qrSize, qrSize);

  // Draw text label
  if (hasText && frame.text) {
    const fontSize = Math.round(16 * (qrSize / 300));
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const textX = canvasW / 2;
    const textBgH = textH;
    const textBgW = ctx.measureText(frame.text.toUpperCase()).width + fontSize * 2;

    if (isTopText) {
      const textY = (topExtra - pad) / 2;
      // Background pill
      ctx.fillStyle = ['badge', 'banner'].includes(frame.style) ? frame.color : '#ffffff';
      ctx.beginPath();
      ctx.roundRect(textX - textBgW / 2, textY - textBgH / 2, textBgW, textBgH, fontSize / 2);
      ctx.fill();
      if (!['badge', 'banner'].includes(frame.style)) {
        ctx.strokeStyle = frame.color;
        ctx.lineWidth = Math.round(1.5 * (qrSize / 300));
        ctx.stroke();
      }
      // Text
      ctx.fillStyle = ['badge', 'banner'].includes(frame.style) ? '#ffffff' : frame.color;
      ctx.fillText(frame.text.toUpperCase(), textX, textY);
    } else {
      const textY = canvasH - (bottomExtra - pad) / 2;
      // Background pill
      ctx.fillStyle = ['badge', 'banner'].includes(frame.style) ? frame.color : '#ffffff';
      ctx.beginPath();
      ctx.roundRect(textX - textBgW / 2, textY - textBgH / 2, textBgW, textBgH, fontSize / 2);
      ctx.fill();
      if (!['badge', 'banner'].includes(frame.style)) {
        ctx.strokeStyle = frame.color;
        ctx.lineWidth = Math.round(1.5 * (qrSize / 300));
        ctx.stroke();
      }
      // Text
      ctx.fillStyle = ['badge', 'banner'].includes(frame.style) ? '#ffffff' : frame.color;
      ctx.fillText(frame.text.toUpperCase(), textX, textY);
    }
  }

  return canvas;
}

export function QRPreview() {
  const ref = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);
  const { currentQR, setCurrentQR } = useQRStore();
  const { user } = useAuthStore();
  const { saveQRCode, dynamicCount, qrCodes } = useQRCodes();
  const { incrementUsage, isAtStaticQRLimit } = useSubscription();
  const navigate = useNavigate();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [qrName, setQrName] = useState('');
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  // Track the saved slug so repeated downloads don't create duplicate DB records
  const savedSlugRef = useRef<string | null>(null);

  const { designSettings, isDynamic } = currentQR;
  
  // For static QR: encode actual content
  // For dynamic QR preview: show placeholder redirect URL format
  const actualContent = generateQRContent(currentQR.type, currentQR.contentData);
  const previewContent = isDynamic 
    ? getRedirectUrl('XXXXXX') // Placeholder for preview
    : actualContent;

  // Helper to map custom types to library-supported types
  const mapDotsType = (type: string): DotType => {
    const supported: DotType[] = ['rounded', 'dots', 'classy', 'classy-rounded', 'square', 'extra-rounded'];
    return (supported.includes(type as DotType) ? type : 'rounded') as DotType;
  };

  const mapCornerSquareType = (type: string): CornerSquareType => {
    const supported: CornerSquareType[] = ['dot', 'square', 'extra-rounded', 'rounded', 'dots', 'classy', 'classy-rounded'];
    return (supported.includes(type as CornerSquareType) ? type : 'square') as CornerSquareType;
  };

  const mapCornerDotType = (type: string): CornerDotType => {
    const supported: CornerDotType[] = ['dot', 'square', 'rounded', 'dots', 'extra-rounded', 'classy', 'classy-rounded'];
    return (supported.includes(type as CornerDotType) ? type : 'dot') as CornerDotType;
  };

  // Memoize QR options
  const qrOptions = useMemo(() => {
    const hasValidLogo = designSettings.logo.enabled && 
                         designSettings.logo.url && 
                         (designSettings.logo.url.startsWith('http') || designSettings.logo.url.startsWith('data:'));

    const options: any = {
      width: 300,
      height: 300,
      data: previewContent || 'https://example.com',
      dotsOptions: {
        color: designSettings.dotsColor,
        type: mapDotsType(designSettings.dotsType),
      },
      backgroundOptions: {
        color: designSettings.backgroundTransparent ? 'transparent' : (
          designSettings.gradient.enabled 
            ? 'transparent' 
            : designSettings.backgroundColor
        ),
      },
      cornersSquareOptions: {
        type: mapCornerSquareType(designSettings.cornersSquareType),
        color: designSettings.cornersSquareColor,
      },
      cornersDotOptions: {
        type: mapCornerDotType(designSettings.cornersDotType),
        color: designSettings.cornersDotColor,
      },
      imageOptions: {
        crossOrigin: 'anonymous' as const,
        margin: hasValidLogo ? 10 : 0,
        imageSize: hasValidLogo ? designSettings.logo.size / 100 : 0,
        hideBackgroundDots: hasValidLogo ? designSettings.logo.transparentBackground : false,
      },
      image: hasValidLogo ? designSettings.logo.url : undefined,
      qrOptions: {
        // Force highest error correction when logo is present to keep QR scannable
        errorCorrectionLevel: hasValidLogo ? 'H' : designSettings.errorCorrectionLevel,
      },
      margin: designSettings.margin,
    };
    return options;
  }, [previewContent, designSettings]);


  // Initialize QR code
  useEffect(() => {
    qrCodeRef.current = new QRCodeStyling(qrOptions);

    if (ref.current) {
      ref.current.innerHTML = '';
      qrCodeRef.current.append(ref.current);
    }
  }, []);

  // Update QR on settings change with debounce
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  useEffect(() => {
    // Clear previous timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    const hasValidLogo = designSettings.logo.enabled && 
                       designSettings.logo.url && 
                       (designSettings.logo.url.startsWith('http') || designSettings.logo.url.startsWith('data:'));

    // Debounce the update
    updateTimeoutRef.current = setTimeout(() => {
      if (qrCodeRef.current) {
        // Calculate background color based on transparency and gradient
        const backgroundColor = designSettings.backgroundTransparent 
          ? 'transparent' 
          : (designSettings.gradient.enabled ? 'transparent' : designSettings.backgroundColor);

        qrCodeRef.current.update({
          data: previewContent || 'https://example.com',
          dotsOptions: {
            color: designSettings.dotsColor,
            type: mapDotsType(designSettings.dotsType),
          },
          backgroundOptions: {
            color: backgroundColor,
          },
          cornersSquareOptions: {
            type: mapCornerSquareType(designSettings.cornersSquareType),
            color: designSettings.cornersSquareColor,
          },
          cornersDotOptions: {
            type: mapCornerDotType(designSettings.cornersDotType),
            color: designSettings.cornersDotColor,
          },
          image: hasValidLogo ? designSettings.logo.url : undefined,
          imageOptions: {
            crossOrigin: 'anonymous' as const,
            margin: hasValidLogo ? 10 : 0,
            imageSize: hasValidLogo ? designSettings.logo.size / 100 : 0,
            hideBackgroundDots: hasValidLogo ? designSettings.logo.transparentBackground : false,
          },
          qrOptions: {
            // Force highest error correction when logo is present to keep QR scannable
            errorCorrectionLevel: hasValidLogo ? 'H' : designSettings.errorCorrectionLevel,
          },
          margin: designSettings.margin,
        } as any);
      }
    }, 300);
    
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [previewContent, designSettings]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    // Check if user is authenticated
    if (!user) {
      // Save current QR design to localStorage
      localStorage.setItem('pending_qr_download', JSON.stringify({
        qr: currentQR,
        timestamp: Date.now()
      }));
      
      toast.info('Please sign in to download your QR code');
      // Redirect to auth with return URL to create page
      navigate('/auth?redirect=/create');
      return;
    }

    // Check limits for static QR codes
    if (!currentQR.isDynamic && isAtStaticQRLimit()) {
      toast.error(`You've reached your static QR code limit. Please upgrade your plan.`);
      navigate('/pricing');
      return;
    }

    if (!qrCodeRef.current) return;

    try {
      setIsDownloading(true);

      // For static QR: use actual content directly
      // For dynamic QR: save to DB first to get real slug
      let downloadData: string;

      if (currentQR.isDynamic) {
        // Check multiple sources to avoid creating duplicate DB records:
        // 1. In-memory ref from this session
        // 2. currentQR.id set from a previous save
        // 3. Match from the fetched qrCodes list
        let existingSlug = savedSlugRef.current;

        if (!existingSlug && currentQR.id) {
          const existing = qrCodes.find((q: any) => q.id === currentQR.id);
          if (existing?.shortSlug) existingSlug = existing.shortSlug;
        }

        if (existingSlug) {
          downloadData = getRedirectUrl(existingSlug);
        } else {
          const savedQR = await saveQRCode.mutateAsync({
            ...currentQR,
            name: currentQR.name || `${currentQR.type}-qr-${Date.now()}`,
          });
          
          if (savedQR?.shortSlug) {
            savedSlugRef.current = savedQR.shortSlug;
            setCurrentQR({ id: savedQR.id });
            downloadData = getRedirectUrl(savedQR.shortSlug);
          } else {
            toast.error('Failed to create dynamic QR code. Please try again.');
            return;
          }
        }
      } else {
        // Static QR: encode the actual content directly
        downloadData = generateQRContent(currentQR.type, currentQR.contentData);
      }

      // Create new QR with download resolution and the real data
      const res = designSettings.resolution;
      const downloadQR = new QRCodeStyling({
        ...qrOptions,
        data: downloadData,
        width: res,
        height: res,
      } as any);

      const { frame } = designSettings;
      const hasFrame = frame.style !== 'none';

      if (!hasFrame) {
        // No frame — download directly
        downloadQR.download({
          name: currentQR.name || 'qr-code',
          extension: designSettings.downloadFormat,
        });
      } else {
        // Frame present — composite QR + frame on canvas
        const qrRaw = await downloadQR.getRawData('png');
        if (!qrRaw) throw new Error('Failed to generate QR image');

        const qrImg = await createImageBitmap(qrRaw as Blob);
        const framedCanvas = drawFrameOnCanvas(qrImg, res, frame);
        
        // Convert canvas to blob and trigger download
        const blob = await new Promise<Blob>((resolve, reject) => {
          const fmt = designSettings.downloadFormat as string;
          const mimeType = fmt === 'jpeg' ? 'image/jpeg' : 'image/png';
          framedCanvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas export failed')), mimeType, 0.95);
        });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${currentQR.name || 'qr-code'}.${designSettings.downloadFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      // Track usage for static QR downloads
      if (!currentQR.isDynamic) {
        incrementUsage({ field: 'static_qr_count', amount: 1 });
      }
      
      toast.success('QR code downloaded successfully!');
      
      // Check if this is user's first download to trigger onboarding tour
      const hasSeenTour = localStorage.getItem('nexqr_tour_completed');
      if (!hasSeenTour) {
        // Set flag to trigger tour after download
        localStorage.setItem('nexqr_show_tour', 'true');
      }
    } catch (error: any) {
      toast.error('Download failed: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!qrName.trim()) return;
    
    const savedQR = await saveQRCode.mutateAsync({
      ...currentQR,
      name: qrName,
    });
    
    // Store the ID so subsequent saves/downloads reuse this record
    if (savedQR?.id) {
      setCurrentQR({ id: savedQR.id });
      if (savedQR.shortSlug) savedSlugRef.current = savedQR.shortSlug;
    }
    
    setSaveDialogOpen(false);
    setQrName('');
  };

  // Frame overlay component
  const FrameOverlay = () => {
    const { frame } = designSettings;
    if (frame.style === 'none') return null;

    const frameClasses = {
      'simple': 'border-4',
      'rounded': 'border-4 rounded-2xl',
      'circle': 'border-4 rounded-full',
      'scan-me-top': 'border-4 rounded-lg',
      'scan-me-bottom': 'border-4 rounded-lg',
      'phone': 'border-[6px] rounded-[2rem] before:content-[""] before:absolute before:top-2 before:left-1/2 before:-translate-x-1/2 before:w-16 before:h-1.5 before:rounded-full before:bg-current before:opacity-30 after:content-[""] after:absolute after:bottom-2 after:left-1/2 after:-translate-x-1/2 after:w-8 after:h-8 after:rounded-full after:border-2 after:border-current after:opacity-30',
      'instagram': 'border-4 rounded-2xl before:content-[""] before:absolute before:-top-1 before:-left-1 before:-right-1 before:-bottom-1 before:rounded-2xl before:bg-gradient-to-br before:from-[#f09433] before:via-[#e6683c] before:to-[#bc1888] before:-z-10',
      'tag': 'border-4 rounded-lg border-b-[16px]',
      'double': 'border-4 ring-4 ring-offset-2',
      'decorative': 'border-0 before:content-[""] before:absolute before:top-0 before:left-0 before:w-8 before:h-8 before:border-t-4 before:border-l-4 before:rounded-tl-lg after:content-[""] after:absolute after:top-0 after:right-0 after:w-8 after:h-8 after:border-t-4 after:border-r-4 after:rounded-tr-lg',
      'badge': 'border-4 rounded-full',
      'ticket': 'border-4 border-dashed rounded-lg',
      'gradient': 'border-4 rounded-xl',
      'neon': 'border-2 shadow-[0_0_15px_currentColor]',
      'clipboard': 'border-x-8 border-b-8 rounded-b-xl before:content-[""] before:absolute before:-top-4 before:left-1/2 before:-translate-x-1/2 before:w-20 before:h-8 before:bg-inherit before:border-4 before:rounded-t-lg',
      'laptop': 'border-8 rounded-t-xl after:content-[""] after:absolute after:-bottom-10 after:-left-4 after:-right-4 after:h-8 after:bg-inherit after:rounded-b-2xl',
      'watch': 'border-4 rounded-[3rem] before:content-[""] before:absolute before:-top-16 before:left-1/2 before:-translate-x-1/2 before:w-24 before:h-16 before:bg-inherit before:rounded-t-3xl after:content-[""] after:absolute after:-bottom-16 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-16 after:bg-inherit after:rounded-b-3xl',
      'camera': 'border-8 rounded-2xl before:content-[""] before:absolute before:-top-6 before:left-12 before:w-16 before:h-6 before:bg-inherit before:rounded-t-lg after:content-[""] after:absolute after:top-4 after:right-4 after:w-6 after:h-6 after:bg-white/20 after:rounded-full',
      'bag': 'border-8 rounded-b-3xl before:content-[""] before:absolute before:-top-12 before:left-1/2 before:-translate-x-1/2 before:w-32 before:h-24 before:border-8 before:border-b-0 before:rounded-t-full',
      'speech-bubble': 'border-4 rounded-3xl after:content-[""] after:absolute after:-bottom-6 after:left-12 after:w-8 after:h-8 after:bg-inherit after:rotate-45 after:border-r-4 after:border-b-4',
      'banner': 'border-y-8 border-x-[24px] border-x-transparent',
    };

    const isTopText = ['scan-me-top', 'laptop', 'watch', 'camera'].includes(frame.style);
    const isBottomText = ['scan-me-bottom', 'tag', 'badge', 'clipboard', 'bag', 'speech-bubble', 'banner'].includes(frame.style);

    return (
      <div 
        className={`absolute inset-0 pointer-events-none ${frameClasses[frame.style as keyof typeof frameClasses] || ''}`}
        style={{ 
          borderColor: frame.style === 'gradient' ? 'transparent' : frame.color,
          borderWidth: frame.style === 'decorative' ? 0 : frame.thickness / 5,
          backgroundColor: ['clipboard', 'laptop', 'watch', 'camera', 'bag', 'speech-bubble', 'phone'].includes(frame.style) ? 'transparent' : undefined,
          ...(frame.style === 'gradient' ? { backgroundImage: `linear-gradient(135deg, ${frame.color}, ${frame.color}88, ${frame.color})`, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude', padding: `${frame.thickness / 5}px` } : {}),
          ...(frame.style === 'decorative' ? { color: frame.color } : {}),
        }}
      >
        {/* Decorative bottom corners (pseudo-elements only cover top corners) */}
        {frame.style === 'decorative' && (
          <>
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 rounded-bl-lg" style={{ borderColor: frame.color }} />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 rounded-br-lg" style={{ borderColor: frame.color }} />
          </>
        )}
        {(isTopText || isBottomText || frame.style === 'badge') && (
          <div
            className={`absolute left-1/2 -translate-x-1/2 px-4 py-1.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap ${
              isTopText || frame.textPosition === 'top' 
                ? '-top-8' 
                : isBottomText || frame.textPosition === 'bottom'
                  ? '-bottom-8'
                  : 'bottom-2'
            }`}
            style={{ 
              backgroundColor: ['badge', 'banner'].includes(frame.style) ? frame.color : 'rgba(255,255,255,0.9)',
              color: ['badge', 'banner'].includes(frame.style) ? '#fff' : frame.color,
              borderRadius: frame.style === 'badge' ? '9999px' : '4px',
              border: !['badge', 'banner'].includes(frame.style) ? `1px solid ${frame.color}` : 'none',
              zIndex: 10
            }}
          >
            {frame.text}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center gap-6"
    >
      {/* Dynamic/Static indicator */}
      <div className="w-full flex items-center justify-center gap-2">
        {isDynamic ? (
          <Badge className="bg-secondary text-secondary-foreground">
            <Zap className="h-3 w-3 mr-1" />
            Dynamic QR
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 ml-1 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-[200px] text-xs">
                  Destination can be changed anytime. Scans are tracked.
                </p>
              </TooltipContent>
            </Tooltip>
          </Badge>
        ) : (
          <Badge variant="outline">
            <Lock className="h-3 w-3 mr-1" />
            Static QR
          </Badge>
        )}
      </div>

      <div 
        className="qr-preview-container surface-elevated rounded-2xl min-h-[350px] w-full flex items-center justify-center relative overflow-visible"
        style={{
          background: designSettings.gradient.enabled && !designSettings.backgroundTransparent
            ? designSettings.gradient.type === 'linear'
              ? `linear-gradient(${designSettings.gradient.rotation}deg, ${designSettings.gradient.color1}, ${designSettings.gradient.color2})`
              : `radial-gradient(circle, ${designSettings.gradient.color1}, ${designSettings.gradient.color2})`
            : undefined
        }}
      >
        <div className="relative">
          <div
            ref={ref}
            className="rounded-xl overflow-hidden shadow-2xl"
          />
          <FrameOverlay />
        </div>
      </div>

      <div className="w-full space-y-4">
        {user ? (
          <Button onClick={handleDownload} disabled={isDownloading} className="w-full gradient-hero hover:opacity-90">
            {isDownloading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {isDownloading ? 'Generating...' : `Download ${designSettings.downloadFormat.toUpperCase()} (${designSettings.resolution}px)`}
          </Button>
        ) : (
          <Button onClick={handleDownload} className="w-full gradient-hero hover:opacity-90">
            <LogIn className="h-4 w-4 mr-2" />
            Sign In to Download
          </Button>
        )}

        {user && (
          <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
            <DialogTrigger
              className={cn(buttonVariants({ variant: 'outline' }), 'w-full')}
            >
              <Save className="h-4 w-4 mr-2" />
              Save to My QR Codes
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Save QR Code
                  {isDynamic && (
                    <Badge className="bg-secondary text-secondary-foreground text-xs">
                      <Zap className="h-3 w-3 mr-0.5" />
                      Dynamic
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="qr-name">QR Code Name</Label>
                  <Input
                    id="qr-name"
                    placeholder="My QR Code"
                    value={qrName}
                    onChange={(e) => setQrName(e.target.value)}
                  />
                </div>
                
                {isDynamic && (
                  <div className="p-3 bg-muted rounded-lg text-xs text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Dynamic QR Benefits:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Change destination anytime without reprinting</li>
                      <li>Track scan analytics (count, location, device)</li>
                      <li>Pause/resume with a toggle</li>
                    </ul>
                    <p className="mt-2 text-xs">
                      Using {dynamicCount + 1} of your dynamic QR quota
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleSave}
                  className="w-full gradient-hero"
                  disabled={!qrName.trim() || saveQRCode.isPending}
                >
                  {saveQRCode.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {user && (
          <SaveTemplateModal 
            qrType={currentQR.type} 
            designSettings={designSettings}
          />
        )}
      </div>
    </motion.div>
  );
}
